import { createContext, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fmt } from "../../lib/model";
import { bigMax, bigMin, divRound, mulQty } from "../../lib/big";
import { useT } from "../../lib/i18n";

/**
 * 以 B 为单位的数后面括号标出折算的金币（leo 2026-10-09）。
 *
 * B 是按进度的：关卡制每关一个、萌宠小镇每颗星一个，用运行时实际的 B
 * （model.ts levelRuntimeB / pettown.ts runtimeB，与服务端发金币同一口径）。
 * 括号里是所有进度里的最小 ~ 最大；鼠标移上去列出每一关（萌宠小镇把 B 相同的相邻星合成一行，否则上千行）。
 * 只看某一关时（通关奖励）传 at，括号里只写那一关。
 */
/** b 是运行时的 B，没有上限（bigint，见 lib/big.ts） */
export interface BScaleRow { n: number; name?: string; b: bigint | null }
export interface BScale {
  unit: "level" | "star"; rows: BScaleRow[];
  /** E（每掷平均产出多少个 B）：括号里顺带写「≈ 几掷的钱」= B 数 ÷ E，运营按掷数理解比按 B 直观 */
  e?: number | null;
  /** 2 = 单掷金币版本：数是「几倍单掷金币」按旧 E 存的 B 数，输入框按 ×g 换算显示（GNumInput），不再标 B */
  model?: 1 | 2;
  /** rows 的 b 要再除以它才是金币（model 2：b = g_milli × 刻度分子，den = 1000 × 刻度分母）。先乘数量再除，与服务端 coinsOf 同一个取整点 */
  den?: bigint;
}

/** 数量 qty 在某一进度上折算的金币 */
export const scaleCoins = (b: bigint, qty: number, den?: bigint): bigint => (den ? divRound(mulQty(b, qty), den) : mulQty(b, qty));

export const BScaleContext = createContext<BScale>({ unit: "level", rows: [] });

export function BCoins({ qty, hi, at }: {
  /** 以 B 计的数；区间（spin 的 min~max）传 qty + hi */
  qty: number | null | undefined; hi?: number | null;
  /** 只看这一个进度（关号 / 星号） */
  at?: number;
}) {
  const t = useT();
  const { unit, rows, e, den } = useContext(BScaleContext);
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  if (qty == null || !Number.isFinite(qty) || !rows.length) return null;
  const top = hi != null && Number.isFinite(hi) && hi > qty ? hi : qty;
  // 掷数与关卡无关（B 数 ÷ E），一个数（或区间）就够
  const perRoll = e != null && Number.isFinite(e) && e > 0 ? e : null;
  const rolls = perRoll == null ? null : qty === top ? fmtRolls(qty / perRoll) : `${fmtRolls(qty / perRoll)}~${fmtRolls(top / perRoll)}`;
  const coins = (b: bigint | null) => (b == null ? "—" : qty === top ? fmt(scaleCoins(b, qty, den)) : `${fmt(scaleCoins(b, qty, den))}~${fmt(scaleCoins(b, top, den))}`);

  if (at != null) {
    const v = coins(rows.find((r) => r.n === at)?.b ?? null);
    return <span className="whitespace-nowrap text-xs text-ink-placeholder">{rolls == null ? t("bcoins.one", { v }) : t("bcoins.oneRolls", { v, r: rolls })}</span>;
  }
  const bs = rows.map((r) => r.b).filter((b): b is bigint => b != null);
  const lo = bs.length ? scaleCoins(bs.reduce(bigMin), qty, den) : null, up = bs.length ? scaleCoins(bs.reduce(bigMax), top, den) : null;
  const summary = lo == null || up == null ? "—" : lo === up ? fmt(lo) : `${fmt(lo)} ~ ${fmt(up)}`;

  const groups: { a: BScaleRow; z: BScaleRow }[] = [];
  for (const r of rows) {
    const g = groups[groups.length - 1];
    if (unit === "star" && g && g.z.b === r.b) g.z = r;
    else groups.push({ a: r, z: r });
  }
  const label = (g: { a: BScaleRow; z: BScaleRow }) =>
    unit === "level" ? t("bcoins.level", { n: g.a.n, name: g.a.name ?? "" })
      : g.a === g.z ? t("bcoins.star", { n: g.a.n }) : t("bcoins.stars", { a: g.a.n, b: g.z.n });
  const show = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: Math.max(12, Math.min(r.left, window.innerWidth - 580)), top: Math.min(r.bottom + 4, window.innerHeight * 0.28) });
  };
  return (
    <span ref={ref} className="cursor-help whitespace-nowrap text-xs text-ink-placeholder underline decoration-dotted underline-offset-2"
      onMouseEnter={show} onMouseLeave={() => setPos(null)}>
      {rolls == null ? t("bcoins.range", { v: summary }) : t("bcoins.rangeRolls", { v: summary, r: rolls })}
      {pos && createPortal(
        <span role="tooltip" style={{ position: "fixed", left: pos.left, top: pos.top, maxHeight: "70vh" }}
          className="pointer-events-none z-[1000] block w-max max-w-[580px] overflow-hidden rounded-base bg-ink px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-md">
          <span className="mb-1 block text-white/70">{t("bcoins.head", { q: qty === top ? fmt(qty) : `${fmt(qty)}~${fmt(top)}` })}</span>
          <span className={`block gap-6 ${groups.length > 12 ? "columns-3" : ""}`}>
            {groups.map((g, k) => (
              <span key={k} className="block whitespace-nowrap tabular-nums">{label(g)}：{coins(g.a.b)}</span>
            ))}
          </span>
        </span>,
        document.body,
      )}
    </span>
  );
}

/** 掷数：不到 1 留两位有效数字（0.017 掷——E 很大时 B 很小，四舍五入成 0 会误导），不到 10 留一位小数，再大取整 */
function fmtRolls(x: number): string {
  if (x > 0 && x < 1) return String(Number(x.toPrecision(2)));
  return x < 10 ? String(Math.round(x * 10) / 10) : fmt(Math.round(x));
}
